import axios from './axios-with-authentication';
import * as backend from './backend-types';

export const AUTH_ME_PATH = '/auth/me';

export type AuthMeResponse = {
  sub?: string;
  id?: string;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
  hco?: string | null;
  locale?: string | null;
  userRole?: string | null;
  organization_name?: string | null;
  healthCareOrganization?: backend.PortalHealthCareOrganizationResponse | null;
  personalData?: backend.PortalPersonalDataResponse;
  phoneNumber?: backend.PortalPhoneNumberResponse;
};

function normalizeOptionalString(value?: string | null): string | undefined {
  const normalized = String(value || '').trim();
  return normalized || undefined;
}

function normalizeApplicationUserRole(value?: string | null): string | undefined {
  const normalized = normalizeOptionalString(value);
  return normalized === 'qarescan_user' || normalized === 'tenant_admin' ? normalized : undefined;
}

function getPersonalData(authMe: AuthMeResponse): backend.PortalPersonalDataResponse | undefined {
  const firstName =
    normalizeOptionalString(authMe.given_name) ||
    normalizeOptionalString(authMe.personalData?.firstName);
  const lastName =
    normalizeOptionalString(authMe.family_name) ||
    normalizeOptionalString(authMe.personalData?.lastName);

  if (firstName || lastName) {
    return {
      ...(firstName ? { firstName } : {}),
      ...(lastName ? { lastName } : {}),
    };
  }

  const fallbackName = normalizeOptionalString(authMe.name);
  return fallbackName ? { firstName: fallbackName } : undefined;
}

export function mapAuthMeToUser(authMe: AuthMeResponse): backend.PortalUserResponse {
  const hcoId =
    normalizeOptionalString(authMe.hco) ||
    normalizeOptionalString(authMe.healthCareOrganization?.id);
  const organizationName =
    normalizeOptionalString(authMe.organization_name) ||
    normalizeOptionalString(authMe.healthCareOrganization?.name) ||
    '';
  const locale =
    normalizeOptionalString(authMe.locale) ||
    normalizeOptionalString(authMe.healthCareOrganization?.locale);
  const email =
    normalizeOptionalString(authMe.email) ||
    normalizeOptionalString(authMe.preferred_username) ||
    '';
  const personalData = getPersonalData(authMe);
  const userRole = normalizeApplicationUserRole(authMe.userRole);

  return {
    id: normalizeOptionalString(authMe.sub) || normalizeOptionalString(authMe.id) || email || '',
    email,
    ...(locale ? { locale } : {}),
    ...(userRole ? { userRole } : {}),
    ...(personalData ? { personalData } : {}),
    ...(authMe.phoneNumber ? { phoneNumber: authMe.phoneNumber } : {}),
    ...(hcoId
      ? {
          healthCareOrganization: {
            ...(authMe.healthCareOrganization || {}),
            id: hcoId,
            name: organizationName,
            ...(locale ? { locale } : {}),
          },
        }
      : {}),
  };
}

let userPromise: Promise<backend.PortalUserResponse> | undefined;

export function invalidateUserCache(): void {
  userPromise = undefined;
}

export async function getCurrentUser(): Promise<backend.PortalUserResponse> {
  if (!userPromise) {
    userPromise = axios
      .get<AuthMeResponse>(AUTH_ME_PATH)
      .then(({ data }) => mapAuthMeToUser(data))
      .catch((err) => {
        userPromise = undefined;
        throw err;
      });
  }
  return userPromise;
}
